import React, { useEffect, useState, useCallback } from "react";
import {
  Grid,
  TextField,
  Autocomplete,
  Select,
  MenuItem,
  IconButton,
  Button,
  Chip,
  ListItemText,
  Box,
  Checkbox,
  Tooltip,
} from "@mui/material";
import { useAppTranslation } from "../../hooks/useAppTranslation";
import LinkIcon from "@mui/icons-material/Link";
import {
  CheckCircleRounded,
  CancelOutlined as CancelIcon,
} from "@mui/icons-material";
import { mergeType } from "../../services/constants";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import { useResourceContext } from "../../contexts/ResourceContext";
import { additionalResourceSchema } from "../../schemas";
import { Popper } from "@mui/base";
import { useRuleResourceContext } from "../../contexts/RuleResourceContext";
import useFetchResources from "../../hooks/useFetchResources";
import { IconDeleteBlueSvg, IconEyeBase } from "../../common/utils/icons";
import {
  mergeResourceColumns,
  buildAdditionalResourceColumnInfo,
} from "../../services/utils/mergeResourceColumns";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// SortableChip component for AdditionalResource - kept outside to avoid hooks-in-loop issues
const AdditionalResourceSortableChip = ({
  option,
  index,
  getTagProps,
}: {
  option: { column_name: string };
  index: number;
  getTagProps: any;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.column_name });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  };

  const { onDelete } = getTagProps({ index });

  const handleDelete = (e: React.MouseEvent | React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDelete) {
      onDelete(e);
    }
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Chip
        label={option.column_name}
        onDelete={onDelete}
        sx={{
          cursor: "grab",
          userSelect: "none",
        }}
        deleteIcon={
          <span
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={handleDelete}
            style={{ cursor: "pointer", padding: "0px 0px" }}
          >
            <CancelIcon style={{ width: 15, height: 15, display: "block" }} />
          </span>
        }
      />
    </div>
  );
};

const AdditionalResource = ({
  resourceColumnId,
  resourceColumns,

  allVariablesList,
  setAllVariablesList,
}: any) => {
  const { t } = useAppTranslation();
  const {
    formData,
    setFormData,
    allResourcesAdditionalData,
    setAllResourcesAdditionalData,
    setIsLoading,
    currentDomainId,
    fileData,
    resourceColumnFileData,
  } = useResourceContext();
  const { globalVariables, setGlobalVariables, availColumns, setAvailColumns } =
    useRuleResourceContext();
  const [resourceColumnData, setResourceColumnData] = useState<any>([]);
  const [useResourceColumns, setUseResourceColumns] = useState<any>([{}]);
  const [validationErrors, setValidationErrors] = useState<
    Record<number, Record<string, string>>
  >({});
  const [resourceColumnIds, setResourceColumnIds] = useState<string[]>([]);
  const [previousResourceId, setPreviousResourceId] = useState<any>(null);

  // @dnd-kit sensors for sortable chips
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Drag-end handler for "Columns to merge (Additional Resource)"
  const handleAddOnColumnDragEnd = (
    event: DragEndEvent,
    additionalRes: any,
  ) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const currentColumns: string[] = additionalRes.add_on_column_names || [];
      const oldIndex = currentColumns.findIndex((col) => col === active.id);
      const newIndex = currentColumns.findIndex((col) => col === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        const newColumns = arrayMove(currentColumns, oldIndex, newIndex);
        setFormData({
          ...formData,
          additional_resource_data: formData.additional_resource_data.map(
            (res: { id: any }) =>
              res.id === additionalRes?.id
                ? { ...res, add_on_column_names: newColumns }
                : res,
          ),
        });
      }
    }
  };

  // Drag-end handler for "Columns to merge (Base Resource)"
  const handleBaseColumnDragEnd = (event: DragEndEvent, additionalRes: any) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const currentColumns: string[] = additionalRes.base_column_names || [];
      const oldIndex = currentColumns.findIndex((col) => col === active.id);
      const newIndex = currentColumns.findIndex((col) => col === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        const newColumns = arrayMove(currentColumns, oldIndex, newIndex);
        setFormData({
          ...formData,
          additional_resource_data: formData.additional_resource_data.map(
            (res: { id: any }) =>
              res.id === additionalRes?.id
                ? { ...res, base_column_names: newColumns }
                : res,
          ),
        });
      }
    }
  };

  const [allResources] = useFetchResources({
    currentDomainId,
    setIsLoading,
  });

  useEffect(() => {
    if (allResources) {
      setAllResourcesAdditionalData(allResources);
    }
  }, [allResources]);

  useEffect(() => {
    const baseColumnsSource =
      fileData?.length > 0
        ? fileData
        : resourceColumnFileData?.length > 0
          ? resourceColumnFileData
          : [];
    const mappedColumns =
      baseColumnsSource.length > 0
        ? baseColumnsSource.map((column: any) => ({
            is_active: column?.is_active,
            column_name: column?.column_name,
          }))
        : [{}];
    setUseResourceColumns({
      resource_column_properties: { resource_columns: mappedColumns },
    });
  }, [fileData, resourceColumnFileData]);

  useEffect(() => {
    if (
      formData?.additional_resource_data &&
      formData?.additional_resource_data.length > 0
    ) {
      const extractedResourceIds = formData?.additional_resource_data.map(
        (resource: { resource_id: any }) => resource.resource_id,
      );
      setResourceColumnIds(extractedResourceIds);
    }
    //dependency makes globalVariables refresh below useEffect on the basis of ResourceColumnIds
  }, [formData?.additional_resource_data]);

  useEffect(() => {
    if (
      resourceColumnIds.length > 0 &&
      resourceColumns.length > 0 &&
      allResourcesAdditionalData.length > 0
    ) {
      const updatedColumnsData = resourceColumnIds.map((res_id: any) => {
        const resource_column_ids = allResourcesAdditionalData?.find(
          (res1: { id: number }) => res1.id === res_id,
        )?.additional_properties?.resource_column_details_id;

        const resource_column = resourceColumns?.find(
          (res1: { id: number }) => res1.id === resource_column_ids,
        )?.resource_column_properties?.resource_columns;
        return {
          resource_id: res_id,
          resource_columns: resource_column,
        };
      });
      setResourceColumnData(updatedColumnsData || []);
      let allAdditionalVariables: any;
      const additionalResVariables = resourceColumnIds.map((res_id: any) => {
        const resource_variables = allResourcesAdditionalData?.find(
          (res1: { id: number }) => res1.id === res_id,
        )?.additional_properties?.inline_variables;
        allAdditionalVariables = {
          ...allAdditionalVariables,
          ...resource_variables,
        };
        return {
          [res_id]: resource_variables,
        };
      });
      setGlobalVariables((prev: any) => ({
        ...prev,
        ...allAdditionalVariables,
      }));
      setAllVariablesList((prev: any) => ({
        ...prev,
        additionalResource: additionalResVariables ?? {},
      }));

      // Merge base resource columns with additional resource columns
      // Build additional resource column info
      const additionalResourceColumnInfo = buildAdditionalResourceColumnInfo(
        formData.additional_resource_data,
        allResourcesAdditionalData,
        updatedColumnsData,
      );

      // Merge columns with conflict resolution
      if (additionalResourceColumnInfo.length > 0) {
        const mergedColumns = mergeResourceColumns(
          availColumns,
          additionalResourceColumnInfo,
        );
        setAvailColumns(mergedColumns);
      }
    }
  }, [resourceColumnIds, resourceColumns, allResourcesAdditionalData]);

  const validateAdditionalResources = (additionalResources: any[]) => {
    const errors: Record<number, Record<string, string>> = {};
    const resourceIds: string[] = [];

    additionalResources.forEach((additionalRes, index) => {
      const { resource_id } = additionalRes;

      // Check uniqueness of resource_id
      if (resourceIds.includes(resource_id)) {
        if (!errors[index]) {
          errors[index] = {};
        }
        errors[index]["resource_id"] = t(
          "resources.additionalResource.errors.resourceAlreadySelected",
        );
      } else {
        resourceIds.push(resource_id);
      }

      try {
        additionalResourceSchema.validateSync(additionalRes, {
          abortEarly: false,
        });
      } catch (validationError: any) {
        validationError.inner.forEach(
          (error: { path: string; message: any }) => {
            const path = error.path as string;
            const errorMessage = error.message;

            // Set the error for the corresponding field in the current resource
            if (!errors[index]) {
              errors[index] = {};
            }
            errors[index][path] = errorMessage;
          },
        );
      }
    });

    setValidationErrors(errors);
  };
  const handleAddAdditionalResource = (e: any) => {
    e.stopPropagation();
    const nextId = formData.additional_resource_data.length + 1;
    const newAdditionalResource = {
      id: nextId,
      resource_id: null,
      base_column_names: [],
      add_on_column_names: [],
      merge_type: "outer",
    };
    const updatedFormData = {
      ...formData,
      additional_resource_data: [
        ...formData.additional_resource_data,
        newAdditionalResource,
      ],
    };
    setFormData(updatedFormData);
    validateAdditionalResources([
      ...formData.additional_resource_data,
      newAdditionalResource,
    ]);
  };
  const renderAdditionalResourcePopper = useCallback((props: any, id: any) => {
    return (
      <Popper
        {...props}
        anchorEl={document.getElementById(
          `autocomplete-additional-resource-${id}`,
        )}
      />
    );
  }, []);
  const handleChangeGenerate = (name: any, value: any, additionalRes: any) => {
    const updatedFormData = {
      ...formData,
      additional_resource_data: formData.additional_resource_data.map(
        (res: { id: any }) =>
          res.id === additionalRes?.id
            ? {
                ...res,
                [name]: value,
              }
            : res,
      ),
    };
    setFormData(updatedFormData);
  };
  const renderBaseResourcePopper = useCallback((props: any, id: any) => {
    return (
      <Popper
        {...props}
        anchorEl={document.getElementById(`autocomplete-base-resource-${id}`)}
      />
    );
  }, []);
  const updateGlobalVariableList = (resourceId: any) => {
    if (!resourceId) {
      console.error("Invalid resourceId:", resourceId);
      return;
    }
    if (
      allVariablesList?.additionalResource &&
      allVariablesList.additionalResource.length > 0
    ) {
      const resourceVar = allVariablesList.additionalResource.find(
        (resource: {}) => Object.keys(resource)[0] === resourceId,
      );
      const matchingResourceVar: any = resourceVar
        ? Object.values(resourceVar)[0]
        : null;
      if (!matchingResourceVar) {
        console.warn("Matching resource not found. Resource ID:", resourceId);
        return; // Exit gracefully or handle the case where matchingResourceVar is null
      }

      const missingKeys = Object.keys(matchingResourceVar).filter((key) => {
        // Check if the key exists in resourceColumn or resource objects
        return allVariablesList?.resource
          ? !Object.keys(allVariablesList?.resource).includes(key)
          : false;
      });

      const filteredGlobalVariable = Object.fromEntries(
        Object.entries(globalVariables).filter(
          ([key]) => !missingKeys.includes(key),
        ),
      );
      setGlobalVariables(filteredGlobalVariable);
    } else {
      console.error(
        "additionalResource is empty or not defined in allVariablesList.",
      );
    }
  };
  return (
    <Grid
      item
      xs={12}
      sm={12}
      md={12}
      lg={12}
      xl={12}
      sx={{ textAlign: "right" }}
    >
      <Button
        variant="contained"
        color="secondary"
        onClick={(e: any) => handleAddAdditionalResource(e)}
        className="btn-orange btn-dark"
        sx={{ marginBottom: "12px" }}
      >
        <AddSharpIcon sx={{ marginRight: "4px" }} />{" "}
        {t("resources.additionalResource.button.add")}
      </Button>
      {formData.additional_resource_data &&
        formData.additional_resource_data?.length > 0 && (
          <>
            <div className="table-responsive">
              <table className="table-filters alternative-table additonal-data-filters">
                <tbody>
                  {formData.additional_resource_data.map(
                    (additionalRes: any, idx: any) => {
                      return (
                        <React.Fragment key={additionalRes?.id}>
                          <tr>
                            <td width={"50"}>
                              <span>{idx + 1}</span>
                            </td>
                            <td width={"18%"}>
                              <Box className="d-flex align-items-end cols-gap-6">
                                <Autocomplete
                                  fullWidth
                                  // options={allResourcesData || []}
                                  options={
                                    allResourcesAdditionalData &&
                                    allResourcesAdditionalData.length > 0
                                      ? allResourcesAdditionalData.filter(
                                          (option: any) =>
                                            option?.id !== formData?.id,
                                        )
                                      : []
                                  }
                                  getOptionLabel={(option) =>
                                    option.resource_name ?? ""
                                  }
                                  value={
                                    allResourcesAdditionalData?.find(
                                      (option: { id: any }) =>
                                        option.id ===
                                        additionalRes?.resource_id,
                                    ) || null
                                  }
                                  renderInput={(params) => (
                                    <TextField
                                      name="resource_id"
                                      style={{ color: "#000000" }}
                                      {...params}
                                      label={
                                        <span>
                                          {t(
                                            "resources.additionalResource.fields.resourceName",
                                          )}{" "}
                                          <span className="required-asterisk">
                                            *
                                          </span>
                                        </span>
                                      }
                                      placeholder={t(
                                        "common.placeholder.select",
                                      )}
                                      InputLabelProps={{
                                        shrink: true,
                                      }}
                                      error={
                                        !!validationErrors[idx]?.resource_id
                                      }
                                      helperText={
                                        validationErrors[idx]?.resource_id || ""
                                      }
                                    />
                                  )}
                                  renderOption={(params, item, { index }) => (
                                    <li
                                      {...params}
                                      key={item.resource_name + index}
                                      style={{
                                        paddingTop: "2px",
                                        paddingBottom: "2px",
                                      }}
                                    >
                                      <ListItemText>
                                        {item?.resource_name}
                                      </ListItemText>
                                    </li>
                                  )}
                                  loadingText="Loading..."
                                  onChange={(e, value) => {
                                    updateGlobalVariableList(
                                      previousResourceId,
                                    );
                                    const updatedFormData = {
                                      ...formData,
                                      additional_resource_data:
                                        formData.additional_resource_data.map(
                                          (res: { id: any }) =>
                                            res.id === additionalRes?.id
                                              ? {
                                                  ...res,
                                                  resource_id: value?.id,
                                                  add_on_column_names: [],
                                                  resource_code: value?.code,
                                                }
                                              : res,
                                        ),
                                    };
                                    setFormData(updatedFormData);
                                    validateAdditionalResources(
                                      updatedFormData?.additional_resource_data,
                                    );
                                    setPreviousResourceId(value?.id);
                                  }}
                                  className={`form-control-autocomplete ${
                                    validationErrors[idx]?.resource_id
                                      ? "has-error"
                                      : ""
                                  }`}
                                />
                                {additionalRes?.resource_id && (
                                  <Box
                                    className="custom-chip"
                                    onClick={() => {
                                      window.open(
                                        `/resource/${formData?.domain_id}/view/${additionalRes?.resource_id}`,
                                        "_blank",
                                      );
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    aria-label="Click to view additional resource"
                                    data-testid={`view-additional-resource-${additionalRes?.resource_id}-btn`}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        window.open(
                                          `/resource/${formData?.domain_id}/view/${additionalRes?.resource_id}`,
                                          "_blank",
                                        );
                                      }
                                    }}
                                  >
                                    <Tooltip
                                      title={t(
                                        "resources.additionalResource.tooltips.viewResource",
                                      )}
                                      placement="top"
                                    >
                                      <IconEyeBase />
                                    </Tooltip>
                                  </Box>
                                )}
                              </Box>
                            </td>
                            <td width={"23%"}>
                              <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={(event) =>
                                  handleAddOnColumnDragEnd(event, additionalRes)
                                }
                              >
                                <Autocomplete
                                  id={`autocomplete-additional-resource-${idx}`}
                                  PopperComponent={(props: any) =>
                                    renderAdditionalResourcePopper(props, idx)
                                  }
                                  fullWidth={true}
                                  className={`form-control-autocomplete autocomplete-pad-right ${
                                    validationErrors[idx]?.add_on_column_names
                                      ? "has-error"
                                      : ""
                                  }`}
                                  multiple
                                  options={
                                    resourceColumnData.find(
                                      (res: { resource_id: any }) =>
                                        res.resource_id ===
                                        additionalRes?.resource_id,
                                    )?.resource_columns || []
                                  }
                                  getOptionLabel={(option: {
                                    column_name: string;
                                  }) => option.column_name ?? " "}
                                  disableCloseOnSelect
                                  onChange={(key: any, value: any) => {
                                    const addOnColumnNames = value.map(
                                      (resource: any) => resource.column_name,
                                    );
                                    const updatedFormData = {
                                      ...formData,
                                      additional_resource_data:
                                        formData.additional_resource_data.map(
                                          (res: { id: any }) =>
                                            res.id === additionalRes?.id
                                              ? {
                                                  ...res,
                                                  add_on_column_names:
                                                    addOnColumnNames,
                                                }
                                              : res,
                                        ),
                                    };
                                    setFormData(updatedFormData);
                                    validateAdditionalResources(
                                      updatedFormData?.additional_resource_data,
                                    );
                                  }}
                                  renderInput={(params) => (
                                    <>
                                      <div className="autocomplete-chips-direction">
                                        <TextField
                                          {...params}
                                          variant="outlined"
                                          label={
                                            <span>
                                              {t(
                                                "resources.additionalResource.fields.columnsToMergeAdditional",
                                              )}
                                              <span className="required-asterisk">
                                                *
                                              </span>
                                            </span>
                                          }
                                          placeholder={t(
                                            "common.placeholder.select",
                                          )}
                                          InputLabelProps={{
                                            shrink: true,
                                          }}
                                          error={
                                            !!validationErrors[idx]
                                              ?.add_on_column_names
                                          }
                                          helperText={
                                            validationErrors[idx]
                                              ?.add_on_column_names || ""
                                          }
                                        />
                                      </div>
                                    </>
                                  )}
                                  renderTags={(value, getTagProps) => (
                                    <SortableContext
                                      items={value.map(
                                        (option: { column_name: string }) =>
                                          option.column_name,
                                      )}
                                      strategy={horizontalListSortingStrategy}
                                    >
                                      <div className="vertical-scroll d-flex align-items-center cols-gap-6">
                                        {value.map((option, index) => (
                                          <AdditionalResourceSortableChip
                                            key={option.column_name}
                                            option={option}
                                            index={index}
                                            getTagProps={getTagProps}
                                          />
                                        ))}
                                      </div>
                                    </SortableContext>
                                  )}
                                  renderOption={(
                                    props,
                                    option,
                                    { selected },
                                  ) => (
                                    <MenuItem
                                      {...props}
                                      key={option.column_name}
                                      value={option.column_name}
                                      sx={{ justifyContent: "space-between" }}
                                    >
                                      {option.column_name}
                                      {selected ? (
                                        <CheckCircleRounded color="info" />
                                      ) : null}
                                    </MenuItem>
                                  )}
                                  // value={
                                  //   additionalRes?.add_on_column_names.length >
                                  //     0 && resourceColumnData.length > 0
                                  //     ? (
                                  //         resourceColumnData.find(
                                  //           (res: { resource_id: any }) =>
                                  //             res.resource_id ===
                                  //             additionalRes?.resource_id
                                  //         ) || {}
                                  //       ).resource_columns?.filter(
                                  //         (option: { column_name: string }) =>
                                  //           additionalRes.add_on_column_names.includes(
                                  //             option.column_name
                                  //           )
                                  //       ) || []
                                  //     : []
                                  // }
                                  value={
                                    additionalRes?.add_on_column_names.length >
                                      0 && resourceColumnData.length > 0
                                      ? additionalRes.add_on_column_names
                                          .map((columnName: any) =>
                                            (
                                              resourceColumnData.find(
                                                (res: { resource_id: any }) =>
                                                  res.resource_id ===
                                                  additionalRes?.resource_id,
                                              ) || {}
                                            ).resource_columns?.find(
                                              (col: { column_name: string }) =>
                                                col.column_name === columnName,
                                            ),
                                          )
                                          .filter(Boolean) // remove undefined if any column is not found
                                      : []
                                  }
                                />
                              </DndContext>
                            </td>
                            <td width={"23%"}>
                              <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={(event) =>
                                  handleBaseColumnDragEnd(event, additionalRes)
                                }
                              >
                                <Autocomplete
                                  id={`autocomplete-base-resource-${idx}`}
                                  PopperComponent={(props: any) =>
                                    renderBaseResourcePopper(props, idx)
                                  }
                                  fullWidth={true}
                                  className={`form-control-autocomplete autocomplete-pad-right ${
                                    validationErrors[idx]?.base_column_names
                                      ? "has-error"
                                      : ""
                                  }`}
                                  multiple
                                  options={
                                    useResourceColumns
                                      ?.resource_column_properties
                                      ?.resource_columns ?? []
                                  }
                                  getOptionLabel={(option: {
                                    column_name: string;
                                  }) => option.column_name ?? " "}
                                  disableCloseOnSelect
                                  onChange={(key: any, value: any) => {
                                    const baseResourceColumns = value.map(
                                      (resource: any) => resource.column_name,
                                    );
                                    const updatedFormData = {
                                      ...formData,
                                      additional_resource_data:
                                        formData.additional_resource_data.map(
                                          (res: { id: any }) =>
                                            res.id === additionalRes?.id
                                              ? {
                                                  ...res,
                                                  base_column_names:
                                                    baseResourceColumns,
                                                }
                                              : res,
                                        ),
                                    };
                                    setFormData(updatedFormData);
                                    validateAdditionalResources(
                                      updatedFormData?.additional_resource_data,
                                    );
                                  }}
                                  renderInput={(params) => (
                                    <>
                                      <div className="autocomplete-chips-direction">
                                        <TextField
                                          {...params}
                                          variant="outlined"
                                          label={
                                            <span>
                                              {t(
                                                "resources.additionalResource.fields.columnsToMergeBase",
                                              )}{" "}
                                              <span className="required-asterisk">
                                                *
                                              </span>
                                            </span>
                                          }
                                          placeholder={t(
                                            "common.placeholder.select",
                                          )}
                                          InputLabelProps={{
                                            shrink: true,
                                          }}
                                          error={
                                            !!validationErrors[idx]
                                              ?.base_column_names
                                          }
                                          helperText={
                                            validationErrors[idx]
                                              ?.base_column_names || ""
                                          }
                                        />
                                      </div>
                                    </>
                                  )}
                                  renderTags={(value, getTagProps) => (
                                    <SortableContext
                                      items={value.map(
                                        (option: { column_name: string }) =>
                                          option.column_name,
                                      )}
                                      strategy={horizontalListSortingStrategy}
                                    >
                                      <div className="vertical-scroll d-flex align-items-center cols-gap-6">
                                        {value.map((option, index) => (
                                          <AdditionalResourceSortableChip
                                            key={option.column_name}
                                            option={option}
                                            index={index}
                                            getTagProps={getTagProps}
                                          />
                                        ))}
                                      </div>
                                    </SortableContext>
                                  )}
                                  renderOption={(
                                    props,
                                    option,
                                    { selected },
                                  ) => (
                                    <MenuItem
                                      {...props}
                                      key={option.column_name}
                                      value={option.column_name}
                                      sx={{ justifyContent: "space-between" }}
                                    >
                                      {option.column_name}
                                      {selected ? (
                                        <CheckCircleRounded color="info" />
                                      ) : null}
                                    </MenuItem>
                                  )}
                                  value={
                                    additionalRes?.base_column_names?.length >
                                      0 &&
                                    useResourceColumns
                                      ?.resource_column_properties
                                      ?.resource_columns
                                      ? additionalRes.base_column_names
                                          .map((columnName: string) =>
                                            useResourceColumns?.resource_column_properties?.resource_columns?.find(
                                              (col: { column_name: string }) =>
                                                col.column_name === columnName,
                                            ),
                                          )
                                          .filter(Boolean)
                                      : []
                                  }
                                />
                              </DndContext>
                            </td>
                            <td width={"23%"}>
                              <label className="label-text">
                                {t(
                                  "resources.additionalResource.fields.mergeType",
                                )}
                                <span className="required-asterisk">*</span>
                              </label>
                              <Select
                                MenuProps={{
                                  disableScrollLock: true,
                                }}
                                title="Merge type"
                                value={additionalRes.merge_type}
                                name="merge_type"
                                style={{ width: "100%", height: 35 }}
                                onChange={(e) => {
                                  const updatedFormData = {
                                    ...formData,
                                    additional_resource_data:
                                      formData.additional_resource_data.map(
                                        (res: { id: any }) =>
                                          res.id === additionalRes?.id
                                            ? {
                                                ...res,
                                                [e.target.name]: e.target.value,
                                              }
                                            : res,
                                      ),
                                  };
                                  setFormData(updatedFormData);
                                }}
                                className={`form-control-autocomplete form-control-autocomplete-1`}
                              >
                                {mergeType.map((type: string) => (
                                  <MenuItem key={type} value={type}>
                                    <span
                                      style={{ textTransform: "capitalize" }}
                                    >
                                      {type}
                                    </span>
                                  </MenuItem>
                                ))}
                              </Select>
                            </td>
                            <td>
                              <label className="label-text"> &nbsp; </label>
                              <IconButton
                                sx={{ color: "grey" }}
                                onClick={() => {
                                  try {
                                    updateGlobalVariableList(
                                      additionalRes.resource_id,
                                    );
                                    setFormData(
                                      (prevFormData: {
                                        additional_resource_data: any[];
                                      }) => {
                                        const updatedAdditionalResource = {
                                          ...prevFormData,
                                          additional_resource_data:
                                            prevFormData.additional_resource_data.filter(
                                              (resource: any) =>
                                                resource.id !==
                                                additionalRes?.id,
                                            ),
                                        };

                                        return updatedAdditionalResource;
                                      },
                                    );
                                  } catch (error: any) {
                                    console.error(
                                      "An error occurred:",
                                      error.message,
                                    );
                                    // Handle the error accordingly, e.g., show a message to the user
                                  }
                                }}
                              >
                                <IconDeleteBlueSvg />
                              </IconButton>
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={6}>
                              <Box
                                sx={{
                                  paddingTop: "7px",
                                  display: "flex",
                                  alignContent: "center",
                                }}
                              >
                                {t(
                                  "resources.additionalResource.fields.generateDerivedColumns",
                                )}
                                <Checkbox
                                  title="use_translation"
                                  name="generate_derived_columns_before_merge_additional_resource"
                                  className="refrence-checkbox custom-checkbox"
                                  checked={
                                    additionalRes?.generate_derived_columns_before_merge_additional_resource ??
                                    false
                                  }
                                  onChange={(e) =>
                                    handleChangeGenerate(
                                      e.target.name,
                                      e.target.checked,
                                      additionalRes,
                                    )
                                  }
                                  sx={{
                                    "&.Mui-checked": {
                                      color: "var(--dark-blue)",
                                    },
                                  }}
                                />
                              </Box>
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    },
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
    </Grid>
  );
};

export default AdditionalResource;
